import { api, ApiError } from '../api/client';
import type { OverviewResponse } from '../api/types';
import { renderRegistrationQr } from '../components/qr';
import { DEVICE_API_ORIGIN, MARKER_STALE_RED_MINUTES, MARKER_STALE_YELLOW_MINUTES } from '../config';
import { batteryColor, colorForId, initialsFromName, stalenessMeta } from '../lib/format';
import { escapeHtml } from '../lib/html';
import { relativeTime, staleness } from '../lib/time';
import { navigate } from '../router';

function formatCountdown(secondsLeft: number): string {
  if (secondsLeft <= 0) return 'expired';
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function mount(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="people-page">
      <div class="people-page-inner">
        <div class="people-page-head">
          <div>
            <h1>People &amp; devices</h1>
            <p class="subtitle">Family members and their devices.</p>
          </div>
          <form id="add-user-form">
            <input type="text" id="new-user-name" class="text-input" placeholder="New member name" required minlength="1" />
            <button type="submit" class="btn-accent">Add member</button>
          </form>
        </div>
        <div id="admin-error" class="admin-error" hidden></div>
        <div id="user-list">Loading&hellip;</div>
      </div>
    </div>
  `;

  const errorBox = root.querySelector<HTMLDivElement>('#admin-error')!;
  const userList = root.querySelector<HTMLDivElement>('#user-list')!;
  const addUserForm = root.querySelector<HTMLFormElement>('#add-user-form')!;
  const newUserNameInput = root.querySelector<HTMLInputElement>('#new-user-name')!;

  let disposed = false;
  const countdownIntervals: number[] = [];

  function clearCountdowns(): void {
    for (const id of countdownIntervals) window.clearInterval(id);
    countdownIntervals.length = 0;
  }

  function showError(err: unknown): void {
    const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
    errorBox.textContent = message;
    errorBox.hidden = false;
  }

  function clearError(): void {
    errorBox.hidden = true;
    errorBox.textContent = '';
  }

  async function load(): Promise<void> {
    try {
      const overview = await api.overview();
      if (disposed) return;
      renderUsers(overview);
    } catch (err) {
      if (disposed) return;
      showError(err);
      userList.innerHTML = '';
    }
  }

  function renderUsers(overview: OverviewResponse): void {
    clearCountdowns();
    userList.innerHTML = '';

    if (overview.users.length === 0) {
      userList.innerHTML = '<p class="member-no-devices">No users yet. Add one above.</p>';
      return;
    }

    for (const user of overview.users) {
      const card = document.createElement('section');
      card.className = 'member-card';

      const head = document.createElement('div');
      head.className = 'member-card-head';
      head.innerHTML = `
        <span class="member-avatar" style="background:${colorForId(user.user_id)}">${escapeHtml(initialsFromName(user.display_name))}</span>
        <div class="member-card-info">
          <div class="member-name">${escapeHtml(user.display_name)}</div>
          <div class="member-devcount">${user.devices.length ? user.devices.length + ' device' + (user.devices.length > 1 ? 's' : '') : 'No devices'}</div>
        </div>
      `;

      const registerBtn = document.createElement('button');
      registerBtn.type = 'button';
      registerBtn.className = 'btn-ghost';
      registerBtn.textContent = 'Register device';
      head.appendChild(registerBtn);

      const archiveBtn = document.createElement('button');
      archiveBtn.type = 'button';
      archiveBtn.className = 'btn-text-danger';
      archiveBtn.textContent = 'Archive';
      head.appendChild(archiveBtn);

      card.appendChild(head);

      if (user.devices.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'member-no-devices';
        empty.textContent = 'No devices registered yet.';
        card.appendChild(empty);
      }

      for (const device of user.devices) {
        const row = document.createElement('div');
        row.className = 'device-row';

        const isActive = device.status === 'active';
        const st = stalenessMeta(staleness(device.last_seen_at, MARKER_STALE_YELLOW_MINUTES, MARKER_STALE_RED_MINUTES));

        row.innerHTML = `
          <span class="status-dot" style="background:${isActive ? st.color : 'var(--faint)'}"></span>
          <div class="device-row-info">
            <div class="device-row-name">${escapeHtml(device.device_name)}</div>
            <div class="device-row-model">${escapeHtml(device.model || 'unknown')}</div>
          </div>
          <div class="device-row-status">
            <div class="last-seen" style="color:${isActive ? st.color : 'var(--faint)'}">${relativeTime(device.last_seen_at)}</div>
            <div class="sc-label">${isActive ? st.label : 'Revoked'}</div>
          </div>
          <div class="device-row-batt" style="color:${batteryColor(device.last_battery)}">${device.last_battery !== null ? device.last_battery + '%' : '—'}</div>
        `;

        const actions = document.createElement('div');
        actions.className = 'device-row-actions';

        if (isActive) {
          const renameBtn = document.createElement('button');
          renameBtn.type = 'button';
          renameBtn.className = 'btn-ghost small';
          renameBtn.textContent = 'Rename';
          renameBtn.addEventListener('click', async () => {
            const name = window.prompt('New device name:', device.device_name);
            if (!name || name === device.device_name) return;
            clearError();
            try {
              await api.renameDevice(device.device_id, name);
              await load();
            } catch (err) {
              showError(err);
            }
          });
          actions.appendChild(renameBtn);
        }

        const logBtn = document.createElement('button');
        logBtn.type = 'button';
        logBtn.className = 'btn-ghost small';
        logBtn.textContent = 'Log';
        logBtn.addEventListener('click', () => {
          navigate(`/people/devices/${encodeURIComponent(device.device_id)}/checkins`);
        });
        actions.appendChild(logBtn);

        if (isActive) {
          const revokeBtn = document.createElement('button');
          revokeBtn.type = 'button';
          revokeBtn.className = 'btn-text-danger small';
          revokeBtn.textContent = 'Revoke';
          revokeBtn.addEventListener('click', async () => {
            if (!window.confirm(`Revoke "${device.device_name}"? It will stop being able to send location updates.`)) {
              return;
            }
            clearError();
            try {
              await api.revokeDevice(device.device_id);
              await load();
            } catch (err) {
              showError(err);
            }
          });
          actions.appendChild(revokeBtn);
        } else {
          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'btn-text-danger small';
          deleteBtn.textContent = 'Delete';
          deleteBtn.addEventListener('click', async () => {
            if (
              !window.confirm(
                `Permanently delete "${device.device_name}"? ALL location history for this device will be erased. This cannot be undone.`,
              )
            ) {
              return;
            }
            clearError();
            try {
              await api.deleteDevice(device.device_id);
              await load();
            } catch (err) {
              showError(err);
            }
          });
          actions.appendChild(deleteBtn);
        }

        row.appendChild(actions);
        card.appendChild(row);
      }

      const qrPanel = document.createElement('div');
      qrPanel.className = 'qr-panel';
      qrPanel.hidden = true;
      card.appendChild(qrPanel);

      registerBtn.addEventListener('click', async () => {
        clearError();
        try {
          const { token, short_code, expires_at } = await api.mintRegistrationToken(user.user_id);
          qrPanel.hidden = false;
          qrPanel.innerHTML = `
            <canvas></canvas>
            <div class="short-code">Manual entry code: <strong>${short_code}</strong></div>
            <div class="expiry-countdown"></div>
          `;
          const canvas = qrPanel.querySelector<HTMLCanvasElement>('canvas')!;
          const countdownEl = qrPanel.querySelector<HTMLDivElement>('.expiry-countdown')!;
          await renderRegistrationQr(canvas, DEVICE_API_ORIGIN, token);

          const tick = (): void => {
            const secondsLeft = expires_at - Math.floor(Date.now() / 1000);
            countdownEl.textContent = secondsLeft > 0 ? `Expires in ${formatCountdown(secondsLeft)}` : 'Expired - mint a new one';
            if (secondsLeft <= 0) window.clearInterval(intervalId);
          };
          tick();
          const intervalId = window.setInterval(tick, 1000);
          countdownIntervals.push(intervalId);
        } catch (err) {
          showError(err);
        }
      });

      archiveBtn.addEventListener('click', async () => {
        if (
          !window.confirm(
            `Archive "${user.display_name}"? This revokes ALL of their devices - they will stop reporting location. This cannot be undone from the portal.`,
          )
        ) {
          return;
        }
        clearError();
        try {
          await api.archiveUser(user.user_id);
          await load();
        } catch (err) {
          showError(err);
        }
      });

      userList.appendChild(card);
    }
  }

  addUserForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = newUserNameInput.value.trim();
    if (!name) return;
    clearError();
    try {
      await api.createUser(name);
      newUserNameInput.value = '';
      await load();
    } catch (err) {
      showError(err);
    }
  });

  load();

  return () => {
    disposed = true;
    clearCountdowns();
  };
}
